/**
 * Audit Log Helper for CFR 21 Part 11 Compliance
 * All audit logs are append-only (no UPDATE/DELETE allowed by RLS).
 * Writes go through the platform IXVerify API (POST /api/ixverify/audit-log)
 * instead of a direct supabase insert.
 */

import { insertAuditLog } from './ixverifyApi';

export type AuditAction =
  | 'login_success'
  | 'login_failed'
  | 'logout'
  | 'session_expired'
  | 'password_changed'
  | 'account_locked'
  | 'account_unlocked'
  | 'user_invited'
  | 'user_created'
  | 'user_role_changed'
  | 'user_deleted'
  | 'verification_created'
  | 'verification_viewed'
  | 'verification_exported';

export type AuditTargetType =
  | 'user'
  | 'verification'
  | 'site'
  | 'invitation'
  | 'system';

export interface AuditLogEntry {
  action: AuditAction;
  targetType: AuditTargetType;
  targetId?: string;
  details?: Record<string, unknown>;
  siteId?: string;
}

/**
 * Log an audit event
 */
export async function logAuditEvent(entry: AuditLogEntry): Promise<void> {
  try {
    const { error } = await insertAuditLog({
      site_id: entry.siteId || null,
      action: entry.action,
      target_type: entry.targetType,
      target_id: entry.targetId || null,
      details: entry.details || null,
    });

    if (error) {
      console.error('[AuditLog] Failed to write audit log:', error);
    }
  } catch (err) {
    console.error('[AuditLog] Error writing audit log:', err);
  }
}

export async function logLoginSuccess(
  userId: string,
  siteId: string | null,
  authMethod: string
): Promise<void> {
  await logAuditEvent({
    action: 'login_success',
    targetType: 'user',
    targetId: userId,
    siteId: siteId || undefined,
    details: { auth_method: authMethod },
  });
}

export async function logLoginFailed(
  email: string,
  reason?: string
): Promise<void> {
  await logAuditEvent({
    action: 'login_failed',
    targetType: 'system',
    details: { email, reason },
  });
}

export async function logLogout(
  userId: string,
  siteId: string | null,
  reason: 'manual' | 'session_expired' = 'manual'
): Promise<void> {
  const action = reason === 'session_expired' ? 'session_expired' : 'logout';
  await logAuditEvent({
    action,
    targetType: 'user',
    targetId: userId,
    siteId: siteId || undefined,
  });
}

export async function logPasswordChanged(
  userId: string,
  siteId: string | null
): Promise<void> {
  await logAuditEvent({
    action: 'password_changed',
    targetType: 'user',
    targetId: userId,
    siteId: siteId || undefined,
  });
}

export async function logAccountLocked(
  email: string,
  attempts: number
): Promise<void> {
  await logAuditEvent({
    action: 'account_locked',
    targetType: 'system',
    details: { email, attempts },
  });
}

export async function logAccountUnlocked(
  userId: string,
  unlockedBy: string,
  siteId: string | null
): Promise<void> {
  await logAuditEvent({
    action: 'account_unlocked',
    targetType: 'user',
    targetId: userId,
    siteId: siteId || undefined,
    details: { unlocked_by: unlockedBy },
  });
}

export async function logUserInvited(
  email: string,
  role: string,
  invitedBy: string,
  siteId: string
): Promise<void> {
  await logAuditEvent({
    action: 'user_invited',
    targetType: 'invitation',
    siteId,
    details: { email, role, invited_by: invitedBy },
  });
}

export async function logUserCreated(
  userId: string,
  email: string,
  siteId: string | null
): Promise<void> {
  await logAuditEvent({
    action: 'user_created',
    targetType: 'user',
    targetId: userId,
    siteId: siteId || undefined,
    details: { email },
  });
}

export async function logUserRoleChanged(
  userId: string,
  oldRole: string,
  newRole: string,
  changedBy: string,
  siteId: string | null
): Promise<void> {
  await logAuditEvent({
    action: 'user_role_changed',
    targetType: 'user',
    targetId: userId,
    siteId: siteId || undefined,
    details: { old_role: oldRole, new_role: newRole, changed_by: changedBy },
  });
}

export async function logUserDeleted(
  userId: string,
  email: string,
  deletedBy: string,
  siteId: string | null
): Promise<void> {
  await logAuditEvent({
    action: 'user_deleted',
    targetType: 'user',
    targetId: userId,
    siteId: siteId || undefined,
    details: { email, deleted_by: deletedBy },
  });
}

export async function logVerificationCreated(
  verificationId: string,
  performedBy: string,
  siteId: string,
  matchResult: string
): Promise<void> {
  await logAuditEvent({
    action: 'verification_created',
    targetType: 'verification',
    targetId: verificationId,
    siteId,
    details: { performed_by: performedBy, match_result: matchResult },
  });
}

export async function logVerificationViewed(
  verificationId: string,
  viewedBy: string,
  siteId: string
): Promise<void> {
  await logAuditEvent({
    action: 'verification_viewed',
    targetType: 'verification',
    targetId: verificationId,
    siteId,
    details: { viewed_by: viewedBy },
  });
}

export async function logVerificationExported(
  verificationId: string,
  exportedBy: string,
  siteId: string
): Promise<void> {
  await logAuditEvent({
    action: 'verification_exported',
    targetType: 'verification',
    targetId: verificationId,
    siteId,
    details: { exported_by: exportedBy },
  });
}
